import {
  BrowserPageNavigationCompletedEventData,
  DefaultWebViewOptions,
  InAppBrowser,
} from '@capacitor/inappbrowser';
import { useQuery } from '@tanstack/react-query';
import { Account } from 'model/IChat';
import { CapacitorStatusBarInitialization } from 'modules/app';
import errorMessages from 'modules/authentication/helpers/authErrors';
import { clearAuthCookies } from 'modules/authentication/helpers/cookies';
import { requestOptionsWithSessionToken } from 'modules/authentication/helpers/requestHelper';
import { unstable_batchedUpdates } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { TrackJS } from 'trackjs';
import { WlWindow } from 'typings/globals/cordova';
import { isAndroidApp, isCordova } from 'utils/platform';
import useAuthStore, { AuthState } from '~/authentication/store';

declare const window: WlWindow;

const IN_APP_BROWSER_AUTH_HOSTS = [
  'https://test.withlocals.com',
  'https://www.withlocals.com',
  'https://withlocals.com',
  URL_AUTHENTICATION_HOST,
];

export const forceTokenRemoval = (error: any, shouldReload: boolean) => {
  if (document.cookie.length < 4000) {
    return;
  }
  window.cookieMaster.clearCookies(
    () => {
      console.error(
        'Cookies have been cleared because of possible 431 response code',
        error,
      );
      useAuthStore.getState().logout();
      if (shouldReload) {
        window.location.reload();
      }
    },
    () => {
      console.error('Cookies could not be cleared');
      if (shouldReload) {
        window.location.reload();
      }
    },
  );
};

export const getCsrfToken = async (): Promise<string | null> => {
  try {
    const csrfResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/csrf`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      },
    );
    const csrf = await csrfResponse.json();
    return csrf.csrfToken;
  } catch (exception) {
    console.error('Error getting CSRF token', exception);
  }
  return null;
};

export const fetchAuthToken = async (token: string | undefined) => {
  try {
    const requestOptions: RequestInit = requestOptionsWithSessionToken(
      {
        method: 'POST',
      },
      token,
    );

    const tokenResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/v1/auth/token`,
      requestOptions,
    );

    const tokenJsonResponse = await tokenResponse.json();

    if (tokenJsonResponse.token) {
      unstable_batchedUpdates(() => {
        useAuthStore.getState().setUser(tokenJsonResponse.user);
        useAuthStore.getState().setSpy(tokenJsonResponse?.spy);
        useAuthStore.getState().setToken(tokenJsonResponse.token);
      });
    } else {
      if (useAuthStore.getState().token) {
        console.error('No valid token received, logging out');
        useAuthStore.getState().logout();
      }
    }
  } catch (exception) {
    console.warn('Error verifying token', exception);
    const shouldCheckForTooLargeCookies = isAndroidApp() && window.cookieMaster;

    if (shouldCheckForTooLargeCookies) {
      forceTokenRemoval(exception, true);
      return;
    }
  }
};

export const fetchAccount = async (
  token: string | undefined,
): Promise<boolean> => {
  try {
    const requestOptions: RequestInit = requestOptionsWithSessionToken(
      {
        method: 'GET',
      },
      token,
    );

    const accountResponse = await fetch(
      `${URL_API_HOST}/api/v1/account`,
      requestOptions,
    );

    if (accountResponse.status !== 200) {
      throw new Error('account');
    }

    const accountJson: Account = await accountResponse.json();

    useAuthStore.setState({
      account: accountJson,
    });
    if (TrackJS && accountJson?.id) {
      TrackJS.addMetadata('account_id', accountJson?.id);
    }
    return true;
  } catch (exception) {
    console.warn(`Error fetching ${exception.message}`);
  }
  return false;
};

export const fetchRealAccount = async (token: string | undefined) => {
  const requestOptions: RequestInit = requestOptionsWithSessionToken(
    {
      method: 'GET',
    },
    token,
  );

  try {
    const realAccountResponse = await fetch(
      `${URL_API_HOST}/api/v1/account/me`,
      requestOptions,
    );

    if (realAccountResponse.status !== 200) {
      throw new Error('real account');
    }

    const realAccountJson = await realAccountResponse.json();

    useAuthStore.setState({ realAccount: realAccountJson });
  } catch (exception) {
    console.warn(`Error fetching ${exception.message}`);
  }
};

export const fetchAccountInformation = async (
  state: AuthState | null | undefined,
  options: {
    silent: boolean;
  } = { silent: false },
): Promise<boolean> => {
  if (!options.silent) {
    useAuthStore.setState({ loading: true });
  }

  await fetchAuthToken(state?.token);
  await fetchAccount(useAuthStore.getState()?.token).then((success) => {
    if (success) {
      // do not await this, as it is not critical for the user experience
      void fetchRealAccount(useAuthStore.getState()?.token);
    }
  });

  useAuthStore.setState({ loading: false });
  return true;
};

export const signIn = async (
  provider: string,
  callbackUrl = '/',
  onFinish: ({
    error,
    success,
    redirectUrl,
  }: {
    error?: any;
    success: boolean;
    redirectUrl?: string;
  }) => void,
): Promise<void> => {
  try {
    await clearAuthCookies();
    const csrfToken = await getCsrfToken();

    const params = new URLSearchParams();
    params.set('callbackUrl', callbackUrl ?? '/');
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');

    const signinResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/signin/${provider}`,
      {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: params.toString(),
      },
    );

    const signin = await signinResponse.json();

    const { url } = signin;
    if (isCordova()) {
      await InAppBrowser.openInWebView({
        url,
        options: {
          ...DefaultWebViewOptions,
          showToolbar: true,
          showNavigationButtons: true,
          showURL: false,
          clearCache: false,
          clearSessionCache: false,
          customWebViewUserAgent: 'Mozilla/5.0 Google',
          iOS: {
            ...DefaultWebViewOptions.iOS,
            surpressIncrementalRendering: false,
          },
        },
      });

      await InAppBrowser.addListener(
        'browserPageNavigationCompleted',
        async (event: BrowserPageNavigationCompletedEventData) => {
          if (
            IN_APP_BROWSER_AUTH_HOSTS.some((host) => event.url.startsWith(host))
          ) {
            if (event.url.includes('api/auth/callback')) {
              void fetchAccountInformation(useAuthStore.getState(), {
                silent: false,
              });
              await InAppBrowser.close();
              return;
            } else {
              void fetchAccountInformation(useAuthStore.getState(), {
                silent: false,
              });
              await InAppBrowser.close();
              onFinish({ success: true, redirectUrl: callbackUrl });
              return;
            }
          }
        },
      );
      await InAppBrowser.addListener('browserClosed', () => {
        CapacitorStatusBarInitialization();
      });
    } else {
      onFinish({ success: true, redirectUrl: url });
    }
  } catch (error) {
    onFinish({ success: false, error });
    console.error('[signIn] Error signing in', error, provider, callbackUrl);
  }
};

export const signInWithCredentials = async (
  username: string,
  password: string,
  callbackUrl: string,
): Promise<void> => {
  try {
    const csrfToken = await getCsrfToken();

    if (!csrfToken) {
      throw new Error('Failed to get CSRF token');
    }

    const params = new URLSearchParams();
    params.set('username', username);
    params.set('password', password);
    params.set('callbackUrl', callbackUrl);
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');
    params.set('redirect', 'false');

    const signinResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/callback/credentials`,
      {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: params.toString(),
      },
    );

    const signin = await signinResponse.json();

    if (!signinResponse.ok) {
      const url = new URL(signin.url);

      const errorSearchParams = new URLSearchParams(url.search);
      throw new Error(errorMessages.get(errorSearchParams.get('error')));
    }

    await fetchAccountInformation(useAuthStore.getState());

    if (isCordova()) {
      window.location.href = callbackUrl;
    } else {
      console.log('signin response', signin);
      const { url } = signin;

      window.location.href = url;
    }
  } catch (exception) {
    console.error(
      '[signInWithCredentials] Error signing in',
      exception,
      callbackUrl,
    );
    throw exception;
  }
};

export const signUpWithCredentials = async ({
  firstName,
  lastName,
  email,
  password,
  gender,
  phoneNumber,
  callbackUrl,
}: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  gender?: string;
  phoneNumber?: string;
  callbackUrl?: string;
}): Promise<void> => {
  const body = {
    firstName,
    email,
    password,
    lastName,
    gender,
    phoneNumber,
  };

  const signupResponse = await fetch(
    `${URL_AUTHENTICATION_HOST}/api/v1/auth/signup/`,
    {
      method: 'POST',
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  if (!signupResponse.ok) {
    const signup = await signupResponse.json();
    throw new Error(signup.error);
  }

  await signInWithCredentials(email, password, callbackUrl);
};

export const signOut = async (callbackUrl: string): Promise<void> => {
  try {
    const csrfToken = await getCsrfToken();

    const params = new URLSearchParams();
    params.set('callbackUrl', callbackUrl);
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');

    const signoutResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/auth/signout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: params.toString(),
      },
    );

    await clearAuthCookies();

    void (await signoutResponse.json());
    unstable_batchedUpdates(() => {
      useAuthStore.getState().logout();
    });
    window.location.href = '/';
  } catch (exception) {
    console.error('Error signing out', exception);
  }
};

const enableAuthCheck = (pathname: string): boolean => {
  return !pathname.includes('personalized-offer');
};
export const AuthCheck = () => {
  const location = useLocation();
  useQuery({
    queryFn: () =>
      fetchAccountInformation(useAuthStore.getState(), { silent: true }),
    queryKey: ['auth'],
    enabled: enableAuthCheck(location.pathname),
    refetchOnMount: false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  return null;
};
